import { useGemMeter } from '@/modules/gem-meter';
import { app } from '@/store';
import getShopDomain from '@/app/utils/getShopDomain';
import { addParamsToUrl } from '@/app/utils/helpers';
import { useAbTestingRedirect } from '@/modules/ab-testing/hooks/useAbTestingRedirect';
import { sentryCaptureException } from '@/exceptions/sentry';

/**
 * liveView
 * @param liveUrl ...
 * @param templateUrl ...
 * @param desc ..
 * @param pageType ..
 * @returns {String} ...
 */
export const liveView = (_params: {
  liveUrl: string;
  templateUrl?: string;
  pageType: string;
  desc?: string;
}): string => {
  const { liveUrl, templateUrl, desc, pageType } = _params;
  const payload = {
    _ab: 0,
    key: new Date().getTime(),
  };
  const urlLiveView = addParamsToUrl(liveUrl, payload);
  const urlTemplate = addParamsToUrl(templateUrl, payload);
  const { degProgressValue } = useGemMeter();
  const percent = (degProgressValue.value / 360) * 100;
  const { idProductDynamic, statusProductDynamic } = app.$store.state['page']['assignedProduct'];

  let viewPageTemplate = `<a target="_blank" href="${urlLiveView}" class="closer btn ${
    !(urlTemplate && urlTemplate.length) ? 'btn-primary' : 'btn-outline btn-clean'
  } btn-default grow">View Live Page</a>`;

  if (urlTemplate && urlTemplate.length) {
    viewPageTemplate = `<a target="_blank" href="${urlTemplate}" class="closer btn btn-primary btn-default grow">Preview Template On Shopify</a>`;
  }

  if (pageType === 'template-product' && ['draft', 'archived'].includes(statusProductDynamic)) {
    const shopDomain = getShopDomain();
    const productAdminUrl = `https://${shopDomain}/admin/products/${idProductDynamic}`;
    viewPageTemplate = `<a target="_blank" href="${productAdminUrl}" class="closer btn btn-primary btn-default grow">Go to Shopify Admin</a>`;
  }

  const descTemplate = `
    <div class="p-2">${desc}</div>
    <div class="border-t-[1px]" style="border-color: rgba(73, 73, 73, 0.1);"></div>
  `;

  const buildAbTipTemplate = (): string => {
    const { redirectUrl, buttonLabel, isConnected, hasCampaign, isCampaignGroup } = useAbTestingRedirect(pageType);
    const linkAttrs = `data-page-type="${pageType}" data-connected="${isConnected}" data-has-campaign="${hasCampaign}" data-campaign-group="${isCampaignGroup}"`;

    return `<div class="bg-light-300 !pt-6 !pl-14 !pr-4 relative">
    <img class="absolute left-[16px]" src="/editor/static/abtest/icons/light.svg" />
    <div class="text-base font-medium text-light-mode-high-contrast">
      Tips for you
    </div>
    <div class="font-normal text-sm">
      Try using A/B testing to compare and find the best-performed version of your page.
    </div>
    <div class="!mt-4 !mb-5">
      <img src="/editor/static/abtest/images/tip.png" />
    </div>
    <div class="!pb-5">
      <a id="gemXAbTestingTipLink" ${linkAttrs} target="_blank" href="${redirectUrl}" class="!text-primary-300 text-sm font-medium">${buttonLabel}</a>
    </div>
  </div>`;
  };

  // The publish tooltip matters more than the A/B tip: if building the tip throws, drop the tip
  // rather than losing the "View Live Page" / "Preview Template On Shopify" button with it.
  let abTipTemplate = '';
  if (percent >= 90) {
    try {
      abTipTemplate = buildAbTipTemplate();
    } catch (error) {
      sentryCaptureException('liveView.buildAbTipTemplate', String(error), { pageType }, { level: 'error' });
    }
  }

  const html = `
  <div>
    <div>
      <div class="flex items-center p-2">
        <svg class="!mr-2" width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M10 18.8833C14.8325 18.8833 18.75 14.938 18.75 10.0711C18.75 5.20418 14.8325 1.25879 10 1.25879C5.16751 1.25879 1.25 5.20418 1.25 10.0711C1.25 14.938 5.16751 18.8833 10 18.8833ZM15.1893 7.74921C15.4348 7.50485 15.4372 7.10632 15.1945 6.85905C14.9519 6.61178 14.5562 6.60943 14.3107 6.85378L8.42558 12.711L6.43933 10.7341C6.19381 10.4897 5.79809 10.4921 5.55546 10.7393C5.31283 10.9866 5.31516 11.3851 5.56067 11.6295L7.98624 14.0437C8.2297 14.286 8.62142 14.286 8.86489 14.0437L15.1893 7.74921Z" fill="#00A042"/>
        </svg>
        <span class="text-base text-light-mode-high-contrast font-medium">
          Successfully publish ${['template-product', 'template-collection'].includes(pageType) ? 'template' : 'page'}
        </span>
      </div>
      <div class="border-t-[1px]" style="border-color: rgba(73, 73, 73, 0.1);"></div>
      <div>${desc ? descTemplate : ''}</div>
      <div class="p-2">${viewPageTemplate}</div>
    </div>
    <div>${abTipTemplate}</div>
  </div>`;
  return html;
};
